import React, { useState, useEffect, useRef } from 'react';
import HeaderContainer from '../core/HeadersAndFooters/HeaderContainer';
import HeaderButtonsForm from '../core/HeadersAndFooters/HeaderButtonsForm';
import FooterContainer from '../core/HeadersAndFooters/FooterContainer';
import StandardButton from '../core/StandardButton';
import ConfirmationModal from '../core/ConfirmationModal';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';
import ImageCropper from '../core/ImageCropper/ImageCropper';
import ArticlePreviewModal from './ArticlePreviewModal';
import { Upload, MapPin, X } from 'lucide-react';
import FormattingToolbar from '../shared/FormattingToolbar';
import { applyTextFormatting } from '../../utils/formatText';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { useTranslation } from '../../i18n/useTranslation';

// Import protected Supabase client and direct fetch helper from shared location
import { supabaseFetch } from '../../lib/supabase';
import { eventService, groupService } from '../../data/dataService';
import { getBoundaryNamesInOrder, getAreaParishes } from '../../data/directoryService';
import ParishSelect from '../shared/ParishSelect';
import { baseParishId } from '../../utils/scopeResolution';
import { getIsPlatformContent } from '../../data/articleMutationService';
import { tokens } from '../../styles/designTokens';
import { addBreadcrumb, addCaughtError } from '../../utils/errorCapture';
import {
  ARTICLE_TITLE_MAX, ARTICLE_SUMMARY_MAX, ARTICLE_CONTENT_MAX,
  ARTICLE_CONTENT_VIDEO_MAX, ARTICLE_IMAGE_CAPTION_MAX, ARTICLE_IMAGE_CREDIT_MAX
} from '../../utils/validationConstants';
import { generateArticleTagPrompt } from '../../utils/aiPromptTemplates';
import { isRateLimited, rateLimitMessage, RATE_LIMIT_TITLE_KEY } from '../../utils/rateLimitMessage';
import { isGroupClosingError, GROUP_CLOSING_MESSAGE_KEY } from '../../utils/groupClosingError';

// Auth token helper - uses supabaseFetch.getAuthToken() which auto-refreshes expired JWTs
const getAuthToken = () => supabaseFetch.getAuthToken();

// The signed-in person, read from the stored session (Architecture: auth from localStorage, never
// the JS client). Not the userId prop: that is whoever's articles are being shown (#78 item 6).
const getSignedInUserId = () => {
  try {
    const key = Object.keys(localStorage).find(k => k.startsWith('sb-') && k.endsWith('-auth-token'));
    const session = key ? JSON.parse(localStorage.getItem(key)) : null;
    return session?.user?.id || null;
  } catch (e) {
    return null;
  }
};

const AddEditArticle = ({ userId, onBack, currentContext, mode = 'add', article = null, preselectedGroupId = null, localityPublishCoords = null, selectedArea = null }) => {
  const { t } = useTranslation('articles');
  // Locality mode: publishing directly to the user's locality with no group required.
  // Active when opened from locality context without a preselected group.
  const isLocalityMode = !preselectedGroupId && !article?.community_id &&
    (currentContext === 'locality' || currentContext?.id === 'locality' || currentContext?.type === 'location');
  const { isMobile } = useMediaQuery();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [user, setUser] = useState(null);
  const [groups, setGroups] = useState([]);
  // #78 outcome 1: true once the group list has loaded, so "saved group not available" is only
  // ever said about a finished list.
  const [groupsLoaded, setGroupsLoaded] = useState(false);
  //const [tags, setTags] = useState([]);
  const [articleTypes, setArticleTypes] = useState([]);

  // Step 7B: home_locality_id tagging state
  const [homeLocalityId, setHomeLocalityId] = useState(null);
  const [homeLocalityBoundaries, setHomeLocalityBoundaries] = useState([]); // [{id, name}] for picker
  const [homeLocalitySource, setHomeLocalitySource] = useState(null); // 'group'|'group-unlocated'|'group-error'|'area-choice'|'no-home'|'existing'
  const [homeLocalityLoading, setHomeLocalityLoading] = useState(true);
  // Group content (#72): the resolver's status for the selected group ('located'|'unlocated'), and
  // the name of the locality the content will appear in, for the read-only line.
  const [groupLocalityStatus, setGroupLocalityStatus] = useState(null);
  const [homeLocalityName, setHomeLocalityName] = useState(null);
  // #78: only a locality the member picks counts as a change; the resolver's default does not.
  const [pickedLocalityId, setPickedLocalityId] = useState(null);
  // #78: true once edit mode has put the saved article into the form.
  const [editLoaded, setEditLoaded] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    summary: '',
    article_type: '',
    content: '',
    cover_image_url: '',
    image_caption: '',
    community_id: '',
    article_tags: [],
    is_public: true,
    video_url: '', // NEW - Video URL field
    website_url: '' // NEW - Website embed URL field
  });

  // Article format state (text, video, or website)
  const [articleFormat, setArticleFormat] = useState('text'); // 'text' | 'video' | 'website'
  const [pendingFormatChange, setPendingFormatChange] = useState(null); // Store pending format change for confirmation

  const [imagePreview, setImagePreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [tagInput, setTagInput] = useState('');
  const [modal, setModal] = useState({ visible: false, message: '', onOk: null });
  const [videoThumbnailUrl, setVideoThumbnailUrl] = useState(null); // Extracted video thumbnail

  // NEW - Hero image state (image system)
  const [heroImageDisplay, setHeroImageDisplay] = useState('');
  const [heroImageThumbnail, setHeroImageThumbnail] = useState('');
  const [heroImageAspect, setHeroImageAspect] = useState('4:3');
  const [heroImageCredit, setHeroImageCredit] = useState('');
  const [showHeroCropper, setShowHeroCropper] = useState(false);

  // Author display name (for credit pre-fill)
  const [authorDisplayName, setAuthorDisplayName] = useState('');

  // NEW - Inline images state (image system)
  const [inlineImages, setInlineImages] = useState([]);
  const [showInlineCropper, setShowInlineCropper] = useState(false);

  // Preview modal state
  const [showPreview, setShowPreview] = useState(false);

  // Cursor position tracking for inline image insertion
  const contentTextareaRef = useRef(null);
  const [contentCursorPos, setContentCursorPos] = useState(0);

  // R3 (docs/briefs/2026-08-15-addeditarticle-is-public-derivation.md): the community_id — and
  // the is_public it was stored under — an edit-mode form was hydrated with. The derivation
  // effect below uses the pair to tell "this is the group the article was already filed under"
  // from "the author just changed it", and to actively RESTORE is_public (not just leave it
  // alone) if the author selects a different group and then selects back to the original one
  // within the same edit session — a plain skip-on-match guard would otherwise leave is_public at
  // whatever the detour derived, since community_id nets out unchanged but is_public was already
  // mutated. Both null in add mode, so the "unchanged" branch never suppresses derivation there.
  const initialCommunityIdRef = useRef(null);
  const initialIsPublicRef = useRef(null);

  // Logo click listener - navigate back to MainFeed
  // Asks first when there are unsaved changes (#78); confirmLeaveRef is set once the hook below runs.
  const confirmLeaveRef = useRef(null);
  useEffect(() => {
    const handleLogoClick = async () => {
      const leave = confirmLeaveRef.current ? await confirmLeaveRef.current() : true;
      if (leave && onBack) onBack();
    };
    window.addEventListener('ournet:context-switch', handleLogoClick);
    return () => window.removeEventListener('ournet:context-switch', handleLogoClick);
  }, [onBack]);

  // Image size options: S = 33%, M = 66%, L = 100%
  const IMAGE_SIZES = {
    S: '33%',
    M: '66%',
    L: '100%'
  };

  // Text formatting toolbar helper - wraps selected text or inserts markers at cursor. The text
  // edit itself is shared with the group Introduction editor (applyTextFormatting).
  const applyFormatting = (type) => {
    const textarea = contentTextareaRef.current;
    if (!textarea) return;

    const result = applyTextFormatting(formData.content, textarea.selectionStart, textarea.selectionEnd, type, {
      bold: t('bold_text'),
      italic: t('italic_text')
    });

    if (result) {
      handleInputChange('content', result.text);
      // Restore focus and cursor position after state update
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(result.cursor, result.cursor);
        setContentCursorPos(result.cursor);
      }, 0);
    }
  };

  // Calculate dynamic header title including selected group/locality name
  const getHeaderTitle = () => {
    const baseTitle = mode === 'edit' ? t('edit_article') : t('new_article');

    if (formData.community_id) {
      const selectedCommunity = groups.find(g => g.id === formData.community_id);
      if (selectedCommunity) {
        return `${baseTitle} (${selectedCommunity.name})`;
      }
    }

    return baseTitle;
  };

  const headerTitle = getHeaderTitle();

  // Read-only status text for the article
  const statusText = (() => {
    if (mode !== 'edit') return t('draft');
    // In edit mode, use the passed article.is_published flag when available
    if (article && (article.is_published === true || article.is_published === false)) {
      return article.is_published ? t('published') : t('draft');
    }
    // Fallback to Draft
    return t('draft');
  })();

  // Ensure tags are always normalized into an array
  const normalizeTags = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    if (typeof val === 'string') {
      // Try parse JSON like '["A","B"]'
      try {
        const parsed = JSON.parse(val);
        if (Array.isArray(parsed)) return parsed;
      } catch (e) {
        // not JSON, fall through to comma-split
      }
      return val.split(',').map(t => t.trim()).filter(Boolean);
    }
    return [];
  };

  // Article types will be loaded from Supabase `articles_type` table

  useEffect(() => {
    // Do nothing until a userId is provided
    if (!userId) {
      console.warn('AddEditArticle: No userId provided, skipping group loading');
      return;
    }

    const loadUserData = async () => {
      try {
        console.log('AddEditArticle: Loading groups for userId:', userId);

        // Load author's display name (for image credit pre-fill)
        const { data: profileData } = await supabaseFetch.query('users', {
          select: 'display_name',
          eq: { id: userId },
          single: true
        });
        if (profileData?.display_name) {
          setAuthorDisplayName(profileData.display_name);
        }

        // Load user's groups from memberships (direct fetch, bypasses JS client which can hang)
        const { data: userGroups, error: groupsError } = await supabaseFetch.query('community_members', {
          select: 'community_id, communities(id, name, postcode, deleted_at)',
          eq: { user_id: userId }
        });

        // Also load groups owned by the user
        const { data: ownedGroups, error: ownedError } = await supabaseFetch.query('communities', {
          select: 'id, name, postcode, deleted_at',
          eq: { owner_id: userId }
        });

        // In edit mode, also fetch the article's current group
        let articleGroup = null;
        let articleGroupReadFailed = false;
        if (mode === 'edit' && article?.community_id) {
          const { data: groupData, error: articleGroupError } = await supabaseFetch.query('communities', {
            select: 'id, name, postcode, deleted_at',
            eq: { id: article.community_id },
            single: true
          });

          if (articleGroupError) {
            console.warn('Error fetching article group:', articleGroupError);
            articleGroupReadFailed = true;
          } else {
            articleGroup = groupData;
          }
        }

        if (groupsError) {
          console.warn('Error loading user groups for', userId, groupsError);
        }
        if (ownedError) {
          console.warn('Error loading owned groups for', userId, ownedError);
        }

        // Combine membership groups, owned groups, and article's group, removing duplicates
        const groupMap = new Map();

        // Add article's group first (to ensure it's always included in edit mode) — unless it is
        // soft-deleted: a deleted group is not a real choice (#78 outcome 1).
        if (articleGroup && articleGroup.id && !articleGroup.deleted_at) {
          groupMap.set(articleGroup.id, {
            id: articleGroup.id,
            name: articleGroup.name,
            postcode: articleGroup.postcode
          });
        }

        // Add membership groups
        if (userGroups) {
          userGroups.forEach(ug => {
            // A membership whose group the viewer cannot read (deleted, or hidden by its privacy)
            // is not a real choice; it used to be listed under its raw id (#78 outcome 1).
            if (!ug.communities || ug.communities.deleted_at) return;
            const id = ug.communities.id;
            if (id && !groupMap.has(id)) {
              groupMap.set(id, {
                id,
                name: ug.communities.name,
                postcode: ug.communities.postcode
              });
            }
          });
        }

        // Add owned groups (if not already present)
        if (ownedGroups) {
          ownedGroups.forEach(og => {
            if (og.id && !og.deleted_at && !groupMap.has(og.id)) {
              groupMap.set(og.id, {
                id: og.id,
                name: og.name,
                postcode: og.postcode
              });
            }
          });
        }

        const groupList = Array.from(groupMap.values());
        console.log('AddEditArticle: Loaded groups:', groupList.length, groupList.map(g => g.name));
        setGroups(groupList);
        // Only a list whose reads all succeeded may be used to say "not available" (#78).
        if (!groupsError && !ownedError && !articleGroupReadFailed) setGroupsLoaded(true);

        // Load available article types from articles_type table
        // Filter to only show 'news' type (events have separate form)
        try {
          const { data: typesData, error: typesError } = await supabaseFetch.query('articles_type', {
            select: 'article_type',
            order: 'article_type.asc'
          });

          if (typesError) throw typesError;

          // Only allow 'news' type for this form
          setArticleTypes((typesData || [])
            .map(t => t.article_type)
            .filter(type => type.toLowerCase() === 'news'));
        } catch (err) {
          console.error('Error loading article types:', err);
          setArticleTypes([]);
        }
      } catch (error) {
        console.error('Error loading user data:', error);
      }
    };

    loadUserData();
  }, [userId, mode, article, isLocalityMode]);

  // If opened in edit mode, populate form with the passed article
  useEffect(() => {
    if (mode !== 'edit' || !article) return;

    // Debug logging for edit mode
    console.log('📸 AddEditArticle edit mode - article received:', article);
    console.log('📸 AddEditArticle edit mode - article.inline_images:', article.inline_images);

    // Map article fields into formData safely
    const mapped = {
      title: article.title || '',
      summary: article.summary || '',
      article_type: article.article_type || '',
      content: article.content || '',
      cover_image_url: article.cover_image_url || '',
      image_caption: article.image_caption || '',
      community_id: article.community_id || '',
      // Normalize tags from possible shapes: array, JSON string, comma-separated string
      article_tags: (function(){
        const t1 = normalizeTags(article.article_tags);
        return t1.length ? t1 : [];
      })(),
      is_public: article.is_public !== undefined ? article.is_public : true,
      video_url: article.video_url || '', // NEW - Video URL field
      website_url: article.website_url || '' // NEW - Website embed URL field
    };

    initialCommunityIdRef.current = mapped.community_id;
    initialIsPublicRef.current = mapped.is_public;
    setFormData(prev => ({ ...prev, ...mapped }));

    // Set article format based on which URL field is populated
    if (article.article_type === 'website' || mapped.website_url) {
      setArticleFormat('website');
    } else if (mapped.video_url) {
      setArticleFormat('video');
    }

    if (mapped.cover_image_url) {
      setImagePreview(mapped.cover_image_url);
      setHeroImageDisplay(mapped.cover_image_url);
    }

    // Load hero image URLs if available
    if (article.hero_image_display_url) {
      setHeroImageDisplay(article.hero_image_display_url);
      setHeroImageThumbnail(article.hero_image_thumbnail_url || '');
      setHeroImageAspect(article.hero_image_aspect || '4:3');
      setImagePreview(article.hero_image_display_url);
    }

    // Load hero image credit if available
    if (article.hero_image_credit) {
      setHeroImageCredit(article.hero_image_credit);
    }

    // Load inline images if available
    if (article.inline_images && Array.isArray(article.inline_images)) {
      setInlineImages(article.inline_images);
    }
    setEditLoaded(true);
  }, [mode, article]);

  // If preselectedGroupId is provided (from "Create New Article" button), set community_id
  useEffect(() => {
    if (preselectedGroupId && mode === 'add') {
      console.log('Setting preselected group:', preselectedGroupId);
      setFormData(prev => ({ ...prev, community_id: preselectedGroupId }));
    }
  }, [preselectedGroupId, mode]);

  // R1 (docs/briefs/2026-08-15-addeditarticle-is-public-derivation.md) + R3: is_public derives
  // from the selected group's privacy for NEW content and for a deliberate group change the
  // author makes in the form — never for existing content left exactly as the author found it.
  // Mirrors AddEditEvent.jsx's identical fix (docs/briefs/2026-08-15-content-keeps-published-
  // privacy.md R2) exactly, including the community_id-changing-to-NULL case: the DB trigger does
  // not derive for a NULL community_id either (fail-closed, addendum-1 §A to that brief), so this
  // form must not force true there or it would show a value the database will not actually store.
  //
  // Reuses eventService.getCommunityPrivacy() (added for AddEditEvent.jsx) rather than a second
  // lookup — content-keeps-published-privacy.md addendum-1 §B: the name is a misnomer now that
  // articles use it too, but moving it is its own scope, flagged in the reuse backlog, not this
  // push.
  useEffect(() => {
    // Unchanged from what this article was already filed under (grouped or ungrouped) — RESTORE
    // what is stored, exactly as the DB trigger would leave it on an ordinary edit. Actively
    // resets rather than just skipping: if the author selected a different group and then
    // selected back to this one within the same edit session, is_public was already mutated by
    // that detour and community_id netting out unchanged does not undo it on its own — the
    // deployed trigger only recomputes on a real OLD/NEW community_id difference, so on save this
    // would otherwise silently carry the detour's derived value through as an "ordinary edit".
    if (mode === 'edit' && formData.community_id === initialCommunityIdRef.current) {
      setFormData(prev => (
        prev.is_public === initialIsPublicRef.current
          ? prev
          : { ...prev, is_public: initialIsPublicRef.current }
      ));
      return;
    }

    if (!formData.community_id) {
      // A deliberate move OUT of a group, mid-edit: the DB trigger does not derive for a NULL
      // community_id either — it leaves is_public exactly as it was under the old group.
      if (mode === 'edit') return;
      // New content with no group selected (or none yet): true is the sensible default, and
      // there is no stored value here to disturb.
      setFormData(prev => (prev.is_public === true ? prev : { ...prev, is_public: true }));
      return;
    }

    let cancelled = false;
    eventService.getCommunityPrivacy(formData.community_id).then(isPrivate => {
      if (cancelled || isPrivate === null) return;
      setFormData(prev => ({ ...prev, is_public: !isPrivate }));
    });
    return () => { cancelled = true; };
  }, [formData.community_id, mode]);

  // Step 7B: Resolve home_locality_id (precedence chain).
  // Content saved into a group follows the group (#72, ruled by Michael 27 Sep 2026 —
  // docs/briefs/2026-09-27-unlocated-group-events-stay-in-group.md, addenda 1-3), whatever context
  // the form was opened from: the group's locality, never the author's own or the one being
  // browsed. A public group with several boundaries offers a picker limited to them (interim,
  // until #73); a group with no location gives no locality (group only, author told); a failed
  // lookup blocks saving. Keyed on formData.community_id — the value that is saved.
  // Edit keeps the stored home_locality_id unless the author changes the group or picks another
  // of its boundaries.
  useEffect(() => {
    const groupId = !isLocalityMode ? formData.community_id : null;

    if (mode === 'edit' && !groupId) {
      if (article?.home_locality_id) {
        setHomeLocalityId(article.home_locality_id);
        setHomeLocalitySource('existing');
      }
      setGroupLocalityStatus(null);
      setHomeLocalityLoading(false);
      return;
    }

    let cancelled = false;

    const resolveHomeLocality = async () => {
      setHomeLocalityLoading(true);

      // Precedence 1: group content follows the group.
      if (groupId) {
        const res = await groupService.resolveGroupHomeLocality(groupId);
        if (cancelled) return;

        if (mode === 'edit' && groupId === initialCommunityIdRef.current) {
          // Unchanged group in edit: keep what is stored; the picker (multi-boundary groups)
          // lets the author move it to another of the group's boundaries.
          const stored = article?.home_locality_id ? Number(article.home_locality_id) : null;
          let options = res.status === 'located' ? res.boundaries : [];
          let storedName = options.find(b => b.id === stored)?.name
            || (stored && stored === res.homeLocalityId ? res.boundaryName : null);
          if (stored && !storedName) {
            const { data } = await getBoundaryNamesInOrder([stored]);
            if (cancelled) return;
            storedName = data[0]?.name || null;
            if (options.length > 1) options = [{ id: stored, name: storedName || String(stored) }, ...options];
          }
          setHomeLocalityId(stored);
          setHomeLocalityName(storedName);
          setHomeLocalityBoundaries(options);
          setGroupLocalityStatus(res.status === 'error' ? null : res.status);
          setHomeLocalitySource('existing');
          setHomeLocalityLoading(false);
          return;
        }

        setHomeLocalityId(res.homeLocalityId);
        setHomeLocalityName(res.boundaryName);
        setHomeLocalityBoundaries(res.boundaries);
        setGroupLocalityStatus(res.status);
        setHomeLocalitySource(res.status === 'located' ? 'group'
          : res.status === 'unlocated' ? 'group-unlocated' : 'group-error');
        setHomeLocalityLoading(false);
        return;
      }

      setGroupLocalityStatus(null);
      setHomeLocalityName(null);

      // Which area a locality post is placed from (own-parish addenda 1–2): the area being browsed
      // (a saved area of the member's own), else their home area. The freshly read home row is
      // used when the browsed area IS home. A temporary visit is not an area and changes nothing
      // here (the post goes to the browsed saved area's parishes, as before).
      let homeArea = null;
      try {
        const { data, error } = await supabaseFetch.query('user_areas', {
          select: 'id,boundary_ids,primary_boundary_id',
          eq: { user_id: userId, is_home: true },
          single: true
        });
        if (!error) homeArea = data || null;
      } catch (err) {
        console.warn('Step 7B: Home area lookup failed:', err);
      }
      if (cancelled) return;

      const browsed = currentContext === 'locality' && selectedArea?.boundary_ids?.length > 0 ? selectedArea : null;
      const area = browsed && (!homeArea || browsed.id !== homeArea.id) ? browsed : homeArea;

      // "Publishing to" chooses among that area's parishes, in their saved order, defaulting to its
      // own (base) parish. The choice is the post's home_locality_id — where it is stored and
      // appears — and its map point follows.
      if (baseParishId(area) != null) {
        const { options, defaultId, error: namesError } = await getAreaParishes(area);
        if (cancelled) return;
        if (namesError) console.warn('Publishing to: parish names could not be read:', namesError);
        setHomeLocalityBoundaries(options);
        setHomeLocalityId(defaultId);
        setHomeLocalityName(options.find(b => b.id === defaultId)?.name || null);
        setHomeLocalitySource('area-choice');
        setHomeLocalityLoading(false);
        return;
      }

      if (cancelled) return;
      // No home area — soft block
      setHomeLocalityId(null);
      setHomeLocalityBoundaries([]);
      setHomeLocalitySource('no-home');
      setHomeLocalityLoading(false);
    };

    resolveHomeLocality();
    return () => { cancelled = true; };
  }, [mode, isLocalityMode, currentContext, formData.community_id, selectedArea, userId, article]);

  // #72: content saved into a group, and whether that group is private (its content appears in no
  // locality feed, so no locality line or picker is shown). is_public derives from the group.
  const isGroupContent = !isLocalityMode && !!formData.community_id;

  // #78 outcome 1: the article is saved in a group this form cannot offer (deleted, left, or not a
  // member). Say so plainly, keep that group as the value, and offer no false choice.
  const savedCommunityId = mode === 'edit' ? (article?.community_id || null) : null;

  // Addendum 4: a platform-wide post keeps its flag and placement exactly as stored; only Content
  // Moderation changes them (migration 20260928f's guard refuses the form otherwise).
  const [isPlatformContent, setIsPlatformContent] = useState(mode === 'edit' && article?.is_platform_content === true);
  useEffect(() => {
    if (mode !== 'edit' || !article?.id) return;
    let cancelled = false;
    getIsPlatformContent(article.id).then(flag => {
      if (!cancelled && flag !== null) setIsPlatformContent(flag);
    });
    return () => { cancelled = true; };
  }, [mode, article?.id]);
  // An edit changes only what the editor changed (addendum 5 ruling 2): the author is never
  // re-written, and a platform post's placement is left as stored (addendum 4), by not sending them.
  // The flag is read fresh at save so a slow first read cannot decide it.
  const keepStoredFields = async (articleData) => {
    if (mode !== 'edit' || !article?.id) return;
    delete articleData.author_id;
    const flag = await getIsPlatformContent(article.id);
    if (flag ?? isPlatformContent) {
      delete articleData.home_locality_id;
      delete articleData.location_point;
      delete articleData.latitude;
      delete articleData.longitude;
    }
  };
  const savedGroupUnavailable = !!savedCommunityId && groupsLoaded
    && formData.community_id === savedCommunityId
    && !groups.some(g => g.id === savedCommunityId);

  // #78 item 6: only an article's author can save it (articles UPDATE policy is author-only). Say
  // so when the form opens and refuse at save, instead of a save that silently changes nothing.
  const [isNotAuthor] = useState(() => {
    if (mode !== 'edit' || !article?.author_id) return false;
    const signedIn = getSignedInUserId();
    return !!signedIn && signedIn !== article.author_id;
  });

  // Where leaving goes: a form opened in a window (My Articles) closes it; otherwise My Articles.
  const leaveForm = () => {
    if (mode === 'edit' && onBack) {
      onBack();
    } else {
      window.dispatchEvent(new CustomEvent('ournet:navigate', { detail: { view: 'articles', scrollToTop: true } }));
    }
  };

  // #78 items 4–5: "unsaved changes" means changed since the form loaded. is_public is left out:
  // it follows the group and the form sets it itself. Only a locality the member picks counts.
  const { is_public: _derivedIsPublic, ...editableFields } = formData;
  const { confirmLeave, markSaved, dialog: leaveDialog } = useUnsavedChanges({
    value: {
      editableFields, articleFormat, heroImageDisplay, heroImageThumbnail,
      heroImageAspect, heroImageCredit, inlineImages, pickedLocalityId
    },
    ready: mode === 'edit'
      ? editLoaded
      : (!preselectedGroupId || formData.community_id === preselectedGroupId),
    onLeave: leaveForm,
  });
  confirmLeaveRef.current = confirmLeave;
  const isPrivateGroupContent = isGroupContent && formData.is_public === false;

  const handleLocalityPick = (value) => {
    const id = value ? Number(value) : null;
    setHomeLocalityId(id);
    setHomeLocalityName(homeLocalityBoundaries.find(b => b.id === id)?.name || null);
    setPickedLocalityId(id);
  };

  // Extract video thumbnail URL when video URL changes
  useEffect(() => {
    const extractVideoThumbnail = async () => {
      if (!formData.video_url || articleFormat !== 'video') {
        setVideoThumbnailUrl(null);
        return;
      }

      const url = formData.video_url;

      // YouTube: Extract video ID and construct thumbnail URL
      if (url.includes('youtube.com') || url.includes('youtu.be')) {
        let videoId;
        if (url.includes('youtu.be/')) {
          videoId = url.split('youtu.be/')[1].split('?')[0].split('&')[0];
        } else if (url.includes('youtube.com')) {
          const urlParams = new URLSearchParams(url.split('?')[1]);
          videoId = urlParams.get('v');
        }

        if (videoId) {
          // Use hqdefault — always exists and avoids grey placeholder issues with maxresdefault
          const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
          setVideoThumbnailUrl(thumbnailUrl);
        }
      }
      // Vimeo: Use oEmbed API to get thumbnail
      else if (url.includes('vimeo.com')) {
        try {
          const response = await fetch(`https://vimeo.com/api/oembed.json?url=${encodeURIComponent(url)}`);
          const data = await response.json();
          if (data.thumbnail_url) {
            setVideoThumbnailUrl(data.thumbnail_url);
          }
        } catch (error) {
          console.error('Error fetching Vimeo thumbnail:', error);
          setVideoThumbnailUrl(null);
        }
      }
      // PeerTube: Try to extract thumbnail from instance
      else if (url.includes('framatube.org') || url.includes('peertube')) {
        try {
          const urlObj = new URL(url);
          const origin = urlObj.origin;

          let videoPath;
          if (url.includes('/w/')) {
            videoPath = url.split('/w/')[1].split('?')[0];
          } else if (url.includes('/videos/watch/')) {
            videoPath = url.split('/videos/watch/')[1].split('?')[0];
          }

          if (videoPath) {
            // PeerTube thumbnail format: {origin}/static/thumbnails/{uuid}.jpg
            const thumbnailUrl = `${origin}/static/thumbnails/${videoPath}.jpg`;
            setVideoThumbnailUrl(thumbnailUrl);
          }
        } catch (error) {
          console.error('Error extracting PeerTube thumbnail:', error);
          setVideoThumbnailUrl(null);
        }
      }
    };

    extractVideoThumbnail();
  }, [formData.video_url, articleFormat]);

  // Use video thumbnail as cover image
  const handleUseVideoThumbnail = () => {
    if (videoThumbnailUrl) {
      setImagePreview(videoThumbnailUrl);
      setFormData(prev => ({ ...prev, cover_image_url: videoThumbnailUrl }));
      setErrors(prev => ({ ...prev, image: null }));
    }
  };

  // NEW - Hero image save handler (image system)
  const handleHeroImageSave = ({ displayUrl, thumbnailUrl, aspectRatio }) => {
    setHeroImageDisplay(displayUrl);
    setHeroImageThumbnail(thumbnailUrl);
    setHeroImageAspect(aspectRatio);
    // Pre-fill credit with author's display name if credit is currently empty
    if (!heroImageCredit && authorDisplayName) {
      setHeroImageCredit(authorDisplayName);
    }
    // Also update the existing cover_image_url for backward compatibility
    setImagePreview(displayUrl);
    setFormData(prev => ({ ...prev, cover_image_url: displayUrl }));
    setErrors(prev => ({ ...prev, image: null }));
  };

  // Helper to build image marker with proper paragraph spacing
  const buildImageMarker = (imageId, content, cursorPos) => {
    const before = content.substring(0, cursorPos);
    const after = content.substring(cursorPos);

    // Check if we need paragraph breaks before/after
    const needsBreakBefore = before.length > 0 && !before.endsWith('\n\n');
    const needsBreakAfter = after.length > 0 && !after.startsWith('\n\n');

    // Build the marker with appropriate spacing
    const prefix = needsBreakBefore ? (before.endsWith('\n') ? '\n' : '\n\n') : '';
    const suffix = needsBreakAfter ? (after.startsWith('\n') ? '\n' : '\n\n') : '';
    const marker = `${prefix}[IMG:${imageId}]${suffix}`;

    return {
      newContent: before + marker + after,
      markerLength: marker.length
    };
  };

  // NEW - Inline image save handler (image system) - inserts at cursor position
  const handleInlineImageSave = ({ displayUrl, thumbnailUrl }) => {
    const newImage = {
      id: `img-${Date.now()}`,
      displayUrl,
      thumbnailUrl,
      position: contentCursorPos,
      size: 'L', // Default to large (100%)
      caption: '',
      credit: authorDisplayName || '' // Pre-fill with author name
    };

    setInlineImages([...inlineImages, newImage]);

    // Insert marker at cursor position with proper paragraph spacing
    const { newContent, markerLength } = buildImageMarker(newImage.id, formData.content, contentCursorPos);
    setFormData(prev => ({ ...prev, content: newContent }));

    // Update cursor position to after the inserted marker
    setContentCursorPos(contentCursorPos + markerLength);
  };

  // Handle inline image size change
  const handleInlineImageSize = (imageId, newSize) => {
    setInlineImages(inlineImages.map(img =>
      img.id === imageId ? { ...img, size: newSize } : img
    ));
  };

  // Handle inline image caption change
  const handleInlineImageCaption = (imageId, caption) => {
    setInlineImages(inlineImages.map(img =>
      img.id === imageId ? { ...img, caption } : img
    ));
  };

  // Handle inline image credit change
  const handleInlineImageCredit = (imageId, credit) => {
    setInlineImages(inlineImages.map(img =>
      img.id === imageId ? { ...img, credit } : img
    ));
  };

  // Handle article format change with confirmation before discarding an entered URL
  const handleFormatChange = (newFormat) => {
    // Leaving video with a video URL entered — confirm before discarding it.
    if (articleFormat === 'video' && newFormat !== 'video' && formData.video_url) {
      setPendingFormatChange(newFormat);
      setModal({
        visible: true,
        title: t('warning'),
        message: t('switch_format_warning'),
        okLabel: t('delete_video'),
        cancelLabel: t('keep_video'),
        danger: true,
        onOk: () => {
          setArticleFormat(newFormat);
          handleInputChange('video_url', '');
          if (newFormat !== 'website') handleInputChange('website_url', '');
          setPendingFormatChange(null);
          setModal({ visible: false, message: '', onOk: null });
        }
      });
    } else if (articleFormat === 'website' && newFormat !== 'website' && formData.website_url) {
      // Leaving website with a website URL entered — parallel confirm.
      setPendingFormatChange(newFormat);
      setModal({
        visible: true,
        title: t('warning'),
        message: t('switch_format_warning'),
        okLabel: t('delete_website'),
        cancelLabel: t('keep_website'),
        danger: true,
        onOk: () => {
          setArticleFormat(newFormat);
          handleInputChange('website_url', '');
          if (newFormat !== 'video') handleInputChange('video_url', '');
          setPendingFormatChange(null);
          setModal({ visible: false, message: '', onOk: null });
        }
      });
    } else {
      // Safe to switch — clear whichever URL is not relevant to the new format.
      setArticleFormat(newFormat);
      if (newFormat !== 'video') handleInputChange('video_url', '');
      if (newFormat !== 'website') handleInputChange('website_url', '');
    }
  };

  //const checkUser = async () => {
  //  const { data: { user } } = await supabase.auth.getUser();
  //  if (!user) {
  //    navigate('/login');
  //    return;
  //  }
  //  setUser(user);
  //};

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      // Website embeds have no visible Story field. Mirror the URL into `content` as it is
      // typed so validation (which runs on formData, BEFORE the save-path backfill) never sees
      // empty content, and the NOT-NULL column is always safe — no per-guard exclusions needed.
      // When the format changes away from website, handleFormatChange clears website_url via this
      // same path (articleFormat is still 'website' in that synchronous handler), clearing content too.
      if (field === 'website_url' && articleFormat === 'website'
          && (!prev.content.trim() || prev.content === prev.website_url)) {
        next.content = value;
      }
      return next;
    });
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim()) {
      const newTags = tagInput
        .split(',')
        .map(tag => tag.trim().toUpperCase())
        .filter(tag => tag && !formData.article_tags.includes(tag));
      
      if (newTags.length > 0) {
        setFormData(prev => ({ ...prev, article_tags: [...prev.article_tags, ...newTags] }));
        setTagInput('');
      }
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      article_tags: prev.article_tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleCopyAIPrompt = () => {
    const prompt = generateArticleTagPrompt(formData);
    navigator.clipboard.writeText(prompt).then(() => {
      // Show success feedback
      setModal({
        visible: true,
        message: t('ai_prompt_copied'),
        onOk: () => setModal({ visible: false, message: '', onOk: null })
      });
    }).catch(err => {
      console.error('Failed to copy prompt:', err);
      setModal({
        visible: true,
        message: t('copy_failed'),
        onOk: () => setModal({ visible: false, message: '', onOk: null })
      });
    });
  };


  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = t('title_required');
    } else if (formData.title.length > ARTICLE_TITLE_MAX) {
      newErrors.title = t('title_max_length');
    }

    // Introduction/Story are optional for a website embed (Title + URL carry it); required otherwise.
    // Max-length is still enforced whenever the field is filled, for every format.
    if (articleFormat !== 'website' && !formData.summary.trim()) {
      newErrors.summary = t('introduction_required');
    } else if (formData.summary.length > ARTICLE_SUMMARY_MAX) {
      newErrors.summary = t('introduction_max_length');
    }

    if (articleFormat !== 'website' && !formData.content.trim()) {
      newErrors.content = t('content_required');
    } else if (formData.content.trim()) {
      // Different character limits based on article format
      const maxContentLength = articleFormat === 'video' ? ARTICLE_CONTENT_VIDEO_MAX : ARTICLE_CONTENT_MAX;
      if (formData.content.length > maxContentLength) {
        newErrors.content = t('content_max_length', { max: maxContentLength });
      }
    }

    if (!isLocalityMode && !formData.community_id) {
      newErrors.community_id = t('select_group_required');
    }

    // A group website embed is anchored by the group (community_id), not by a locality — it's
    // pinned to the group front page and never enters a locality feed, so it must NOT require
    // home_locality. home_locality_id is still stored if it resolves (harmless). Website-only:
    // every other format / locality-mode article keeps the home_locality requirement unchanged.
    const isGroupWebsiteArticle = articleFormat === 'website' && !!formData.community_id;

    // #72: a failed group lookup blocks saving for every format, website included — it never
    // guesses a locality and never saves group content as unlocated by accident.
    if (homeLocalitySource === 'group-error') {
      newErrors.home_locality = t('common:generic_error');
    } else if (!isGroupWebsiteArticle && homeLocalitySource === 'no-home') {
      // Step 7B: Block submission if user has no home area
      newErrors.home_locality = t('no_home_prompt');
    } else if (!isGroupWebsiteArticle && !homeLocalityId && !formData.community_id) {
      // Belt-and-braces: non-group content must have a locality. Group content may be
      // deliberately unlocated (a group with no location, or an existing one with none stored).
      newErrors.home_locality = t('no_home_prompt');
    }

    // Validate video URL if video format selected
    if (articleFormat === 'video' && !formData.video_url.trim()) {
      newErrors.video_url = t('video_url_required');
    }

    // Validate website URL if website format selected
    if (articleFormat === 'website' && !formData.website_url.trim()) {
      newErrors.website_url = t('website_url_required');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveAsDraft = async () => {
    addBreadcrumb('form-submit', { action: 'article-draft', component: 'AddEditArticle' });
    //if (!userId) return;

    // Validate that a group is selected (not required in locality mode)
    if (!isLocalityMode && !formData.community_id) {
      setErrors(prev => ({ ...prev, community_id: t('select_group_required') }));
      setModal({
        visible: true,
        message: t('select_group_message'),
        onOk: () => {
          setModal({ visible: false, message: '', onOk: null });
        }
      });
      return;
    }

    // #72: a failed group lookup blocks the draft too.
    if (homeLocalitySource === 'group-error') {
      setModal({
        visible: true,
        message: t('common:generic_error'),
        onOk: () => {
          setModal({ visible: false, message: '', onOk: null });
        }
      });
      return;
    }

    // Step 7B: Block draft save if user has no home area
    if (homeLocalitySource === 'no-home') {
      setErrors(prev => ({ ...prev, home_locality: t('no_home_prompt') }));
      setModal({
        visible: true,
        message: t('no_home_prompt'),
        onOk: () => {
          setModal({ visible: false, message: '', onOk: null });
        }
      });
      return;
    }

    if (isNotAuthor) {
      setModal({ visible: true, message: t('only_author_can_save'), onOk: () => setModal({ visible: false, message: '', onOk: null }) });
      return;
    }

    setLoading(true);
    try {
      // Get auth token for RLS (read from localStorage, bypasses JS client which can hang)
      const authToken = await getAuthToken();

      let coverImageUrl = formData.cover_image_url;

      // Auto-derive cover thumbnail from video URL if none set (video articles)
      if (!coverImageUrl && videoThumbnailUrl) {
        coverImageUrl = videoThumbnailUrl;
      }

      // Resolve location: from selected user area (locality mode) or from group's boundary_ids (group mode)
      let locationLat = null;
      let locationLng = null;
      if (isLocalityMode && !formData.community_id) {
        // A locality post: the centroid of the parish it is stored in (home_locality_id — the
        // member's own parish, or the parish they chose), not of their whole area
        // (docs/briefs/2026-09-29-own-parish-everywhere.md). The same rule as group content.
        const point = await groupService.getBoundaryCentroid(homeLocalityId);
        locationLat = point?.lat ?? null;
        locationLng = point?.lng ?? null;
      } else if (formData.community_id) {
        // Group content: the centroid of the boundary it is placed in; no point when the group
        // has no location (#72 follow-ups Part 4). No legacy community_localities fallback.
        const point = await groupService.getBoundaryCentroid(homeLocalityId);
        locationLat = point?.lat ?? null;
        locationLng = point?.lng ?? null;
      }
      console.log('Location for article (draft):', { locationLat, locationLng, isLocalityMode, homeLocalityId, community_id: formData.community_id });

      // WARNING: Detect if community_id is unexpectedly missing (possible race condition)
      if (!isLocalityMode && !formData.community_id) {
        console.error('WARNING: Saving group article with no community_id', { formData, isLocalityMode });
      }

      const articleData = {
        // Saved exactly as typed (#93): safety is at display.
        title: formData.title || 'Untitled Draft',
        summary: formData.summary || '',
        content: articleFormat === 'website' && !formData.content.trim() ? formData.website_url : (formData.content || ''), // NOT-NULL-safe backfill: blank website story → plain URL
        article_type: articleFormat === 'website' ? 'website' : 'news', // 'website' embed, else news (text/video are both 'news')
        cover_image_url: coverImageUrl,
        image_caption: formData.image_caption || null,
        community_id: isLocalityMode ? null : (formData.community_id && formData.community_id.trim() !== '' ? formData.community_id : null),
        author_id: userId,
        is_public: false, // Drafts are not public
        is_published: false,
        updated_at: new Date().toISOString(),
        article_tags: formData.article_tags.length > 0 ? formData.article_tags : null,
        video_url: articleFormat === 'video' ? (formData.video_url || null) : null, // NEW - Video URL (only for video format)
        website_url: articleFormat === 'website' ? (formData.website_url || null) : null, // NEW - Website embed URL (only for website format)
        // Location set from group
        latitude: locationLat,
        longitude: locationLng,
        location_point: locationLat && locationLng
          ? `SRID=4326;POINT(${locationLng} ${locationLat})`
          : null,
        // Step 7B: home_locality_id tagging
        home_locality_id: homeLocalityId || null,
        // NEW - Hero and inline images (image system)
        hero_image_display_url: heroImageDisplay || null,
        hero_image_thumbnail_url: heroImageThumbnail || null,
        hero_image_aspect: heroImageAspect || '4:3',
        hero_image_credit: heroImageCredit || null,
        inline_images: inlineImages.length > 0 ? inlineImages : []
      };

      await keepStoredFields(articleData);

      console.log('Saving draft - location fields:', { latitude: articleData.latitude, longitude: articleData.longitude, location_point: articleData.location_point });

      let data, error;
      if (mode === 'edit' && article && article.id) {
        ({ data, error } = await supabaseFetch.update('articles', articleData, {
          eq: { id: article.id },
          authToken
        }));
      } else {
        ({ data, error } = await supabaseFetch.insert('articles', articleData, {
          authToken
        }));
      }

      console.log('Draft save result:', { data, error });
      if (error) throw error;
      if (!data?.id) throw { notSaved: true };

      // Notify that articles have changed so the feed can reload
      window.dispatchEvent(new CustomEvent('ournet:articlesChanged'));

      // Show modal instead of alert; navigation will happen when user confirms
      markSaved();
      setModal({
        visible: true,
        message: t('draft_saved'),
        onOk: () => {
          setModal({ visible: false, message: '', onOk: null });
          leaveForm();
        }
      });
    } catch (error) {
      console.error('Error saving draft:', error);
      addCaughtError(error.message || String(error), 'AddEditArticle:saveDraft');
      const draftErrorMessage = error?.notSaved
        ? t('save_not_applied')
        : isGroupClosingError(error)
          ? t(GROUP_CLOSING_MESSAGE_KEY)
          : isRateLimited(error)
            ? rateLimitMessage(error, t)
            : t('draft_save_failed');
      setModal({
        visible: true,
        title: isRateLimited(error) ? t(RATE_LIMIT_TITLE_KEY) : undefined,
        message: draftErrorMessage,
        onOk: () => {
          setModal({ visible: false, message: '', onOk: null });
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePostArticle = async () => {
    addBreadcrumb('form-submit', { action: 'article-publish', component: 'AddEditArticle' });
    if (!validateForm()) {
      setModal({
        visible: true,
        message: t('fill_required_fields'),
        onOk: () => {
          setModal({ visible: false, message: '', onOk: null });
        }
      });
      return;
    }

    if (isNotAuthor) {
      setModal({ visible: true, message: t('only_author_can_save'), onOk: () => setModal({ visible: false, message: '', onOk: null }) });
      return;
    }

    setLoading(true);
    try {
      // Get auth token for RLS (read from localStorage, bypasses JS client which can hang)
      const authToken = await getAuthToken();

      let coverImageUrl = formData.cover_image_url;

      // Auto-derive cover thumbnail from video URL if none set (video articles)
      if (!coverImageUrl && videoThumbnailUrl) {
        coverImageUrl = videoThumbnailUrl;
      }

      // Resolve location: from selected user area (locality mode) or from group's boundary_ids (group mode)
      let locationLat = null;
      let locationLng = null;
      if (isLocalityMode && !formData.community_id) {
        // A locality post: the centroid of the parish it is stored in (home_locality_id — the
        // member's own parish, or the parish they chose), not of their whole area
        // (docs/briefs/2026-09-29-own-parish-everywhere.md). The same rule as group content.
        const point = await groupService.getBoundaryCentroid(homeLocalityId);
        locationLat = point?.lat ?? null;
        locationLng = point?.lng ?? null;
      } else if (formData.community_id) {
        // Group content: the centroid of the boundary it is placed in; no point when the group
        // has no location (#72 follow-ups Part 4). No legacy community_localities fallback.
        const point = await groupService.getBoundaryCentroid(homeLocalityId);
        locationLat = point?.lat ?? null;
        locationLng = point?.lng ?? null;
      }
      console.log('Location for article (publish):', { locationLat, locationLng, isLocalityMode, homeLocalityId, community_id: formData.community_id });

      // WARNING: Detect if community_id is unexpectedly missing (possible race condition)
      if (!isLocalityMode && !formData.community_id) {
        console.error('WARNING: Publishing group article with no community_id', { formData, isLocalityMode });
      }

      const articleData = {
        // Saved exactly as typed (#93): safety is at display.
        title: formData.title || '',
        summary: formData.summary || '',
        content: articleFormat === 'website' && !formData.content.trim() ? formData.website_url : (formData.content || ''), // NOT-NULL-safe backfill: blank website story → plain URL
        article_type: articleFormat === 'website' ? 'website' : 'news', // 'website' embed, else news (text/video are both 'news')
        cover_image_url: coverImageUrl,
        image_caption: formData.image_caption || null,
        community_id: isLocalityMode ? null : (formData.community_id && formData.community_id.trim() !== '' ? formData.community_id : null),
        author_id: userId,
        is_public: formData.is_public, // derives from the selected group's privacy — see the derivation effect above
        is_published: true,
        updated_at: new Date().toISOString(),
        article_tags: formData.article_tags.length > 0 ? formData.article_tags : null,
        video_url: articleFormat === 'video' ? (formData.video_url || null) : null, // NEW - Video URL (only for video format)
        website_url: articleFormat === 'website' ? (formData.website_url || null) : null, // NEW - Website embed URL (only for website format)
        // Location set from group
        latitude: locationLat,
        longitude: locationLng,
        location_point: locationLat && locationLng
          ? `SRID=4326;POINT(${locationLng} ${locationLat})`
          : null,
        // Step 7B: home_locality_id tagging
        home_locality_id: homeLocalityId || null,
        // NEW - Hero and inline images (image system)
        hero_image_display_url: heroImageDisplay || null,
        hero_image_thumbnail_url: heroImageThumbnail || null,
        hero_image_aspect: heroImageAspect || '4:3',
        hero_image_credit: heroImageCredit || null,
        inline_images: inlineImages.length > 0 ? inlineImages : []
      };

      await keepStoredFields(articleData);

      // Debug logging
      console.log('📸 AddEditArticle saving - inlineImages state:', inlineImages);
      console.log('📸 AddEditArticle saving - articleData.inline_images:', articleData.inline_images);
      console.log('Publishing - location fields:', { latitude: articleData.latitude, longitude: articleData.longitude, location_point: articleData.location_point });

      let data, error;
      if (mode === 'edit' && article && article.id) {
        ({ data, error } = await supabaseFetch.update('articles', articleData, {
          eq: { id: article.id },
          authToken
        }));
      } else {
        ({ data, error } = await supabaseFetch.insert('articles', articleData, {
          authToken
        }));
      }

      console.log('Publish save result:', { data, error });
      if (error) throw error;
      if (!data?.id) throw { notSaved: true };

      // Notify that articles have changed so the feed can reload
      window.dispatchEvent(new CustomEvent('ournet:articlesChanged'));

      // Show modal instead of alert; navigation will happen when user confirms
      markSaved();
      setModal({
        visible: true,
        message: mode === 'edit' ? t('article_updated') : t('article_posted'),
        onOk: () => {
          setModal({ visible: false, message: '', onOk: null });
          // #78 item 2: a form opened in a window closes it.
          leaveForm();
        }
      });
    } catch (error) {
      console.error('Error posting article:', error);
      console.error('Full error details:', JSON.stringify(error, null, 2));
      addCaughtError(error.message || String(error), 'AddEditArticle:publish');
      const publishErrorMessage = error?.notSaved
        ? t('save_not_applied')
        : isGroupClosingError(error)
          ? t(GROUP_CLOSING_MESSAGE_KEY)
          : isRateLimited(error)
            ? rateLimitMessage(error, t)
            : t('article_post_failed', { error: error.message || error.toString() });
      setModal({
        visible: true,
        title: isRateLimited(error) ? t(RATE_LIMIT_TITLE_KEY) : undefined,
        message: publishErrorMessage,
        onOk: () => {
          setModal({ visible: false, message: '', onOk: null });
        }
      });
    } finally {
      setLoading(false);
    }
  };

  // Cancel asks only when something changed (#78), then leaves the way the form was opened.
  const handleCancel = async () => {
    if (await confirmLeave()) leaveForm();
  };

 
  return (
    <div className="min-h-screen bg-[#EDF2F8] flex flex-col">
      {/* Header */}
      <HeaderContainer />
      <HeaderButtonsForm
        currentContext={currentContext}
        onBack={onBack}
        headerTitle={headerTitle}
        onCheckUnsaved={confirmLeave}
      />

      {/* Main Content */}
      <div className="flex-1 pt-[100px]" style={{ paddingBottom: isMobile ? '120px' : '220px' }}>
        <div className="max-w-[1600px]" style={{ marginTop: '0px', marginLeft: isMobile ? '16px' : '40px', marginRight: isMobile ? '16px' : '0px' }}>
          <div className={isMobile ? 'flex flex-col gap-6' : 'flex gap-8'}>

            {/* Article Format - Shows FIRST on mobile, LAST on desktop */}
            {isMobile && (
              <div className="space-y-4">
                {/* Article Format Radio Buttons */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    {t('article_format')}
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="articleFormat"
                        value="text"
                        checked={articleFormat === 'text'}
                        onChange={(e) => handleFormatChange(e.target.value)}
                        className="w-5 h-5 text-[var(--theme-primary)] focus:ring-[var(--theme-primary)] cursor-pointer"
                      />
                      <span className="ml-2 text-sm font-medium text-gray-700">{t('text')}</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="articleFormat"
                        value="video"
                        checked={articleFormat === 'video'}
                        onChange={(e) => handleFormatChange(e.target.value)}
                        className="w-5 h-5 text-[var(--theme-primary)] focus:ring-[var(--theme-primary)] cursor-pointer"
                      />
                      <span className="ml-2 text-sm font-medium text-gray-700">{t('video')}</span>
                    </label>
                    {/* Website format — group content only (embeds carry a community_id) */}
                    {!isLocalityMode && (
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="articleFormat"
                          value="website"
                          checked={articleFormat === 'website'}
                          onChange={(e) => handleFormatChange(e.target.value)}
                          className="w-5 h-5 text-[var(--theme-primary)] focus:ring-[var(--theme-primary)] cursor-pointer"
                        />
                        <span className="ml-2 text-sm font-medium text-gray-700">{t('website')}</span>
                      </label>
                    )}
                  </div>
                </div>

                {/* Video URL Field - Only visible when Video format selected */}
                {articleFormat === 'video' && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('video_url')}
                    </label>
                    <p className="text-xs text-gray-600 mb-3">
                      {t('video_url_hint')}
                    </p>
                    <input
                      type="url"
                      value={formData.video_url}
                      onChange={(e) => handleInputChange('video_url', e.target.value)}
                      placeholder={t('video_url_placeholder')}
                      className={`w-full px-4 py-3 rounded-lg border ${
                        errors.video_url ? 'border-red-500' : 'border-gray-300'
                      } focus:outline-none focus:border-[var(--theme-primary)] text-sm`}
                    />
                    {errors.video_url && (
                      <p className="text-red-500 text-xs mt-1">{errors.video_url}</p>
                    )}
                    {formData.video_url && !errors.video_url && (
                      <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <p className="text-xs text-blue-700 font-semibold mb-1">{t('video_will_embed')}</p>
                        <p className="text-xs text-blue-600 break-all">{formData.video_url}</p>
                      </div>
                    )}
                    <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <p className="text-xs text-yellow-700 font-semibold mb-1">{t('note')}</p>
                      <p className="text-xs text-yellow-600">
                        {t('video_character_limit')}
                      </p>
                    </div>
                  </div>
                )}

                {/* Website URL Field - Only visible when Website format selected */}
                {articleFormat === 'website' && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('website_url')}
                    </label>
                    <p className="text-xs text-gray-600 mb-3">
                      {t('website_url_hint')}
                    </p>
                    <input
                      type="url"
                      value={formData.website_url}
                      onChange={(e) => handleInputChange('website_url', e.target.value)}
                      placeholder={t('website_url_placeholder')}
                      className={`w-full px-4 py-3 rounded-lg border ${
                        errors.website_url ? 'border-red-500' : 'border-gray-300'
                      } focus:outline-none focus:border-[var(--theme-primary)] text-sm`}
                    />
                    {errors.website_url && (
                      <p className="text-red-500 text-xs mt-1">{errors.website_url}</p>
                    )}
                    {formData.website_url && !errors.website_url && (
                      <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <p className="text-xs text-blue-700 font-semibold mb-1">{t('website_will_embed')}</p>
                        <p className="text-xs text-blue-600 break-all">{formData.website_url}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Left Column - Fixed 480px on desktop, full width on mobile - shows THIRD on mobile */}
            <div className="space-y-3" style={isMobile ? { width: '100%', order: 3 } : { width: '480px', flexShrink: 0 }}>
              {/* Hero Image (NEW - Image System) */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('hero_image')}
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center bg-white" style={isMobile ? { width: '100%', aspectRatio: '4 / 3' } : { width: '448px', height: '336px' }}>
                  {heroImageDisplay ? (
                    <div className="relative h-full">
                      <img
                        src={heroImageDisplay}
                        alt="Hero preview"
                        className="w-full h-full object-cover rounded-lg"
                      />
                      <button
                        onClick={() => setShowHeroCropper(true)}
                        className="absolute top-2 right-2 bg-[var(--theme-primary)] text-white px-3 py-1 rounded-lg text-sm hover:bg-[#023872]"
                      >
                        {t('change')}
                      </button>
                      <button
                        onClick={() => {
                          setHeroImageDisplay('');
                          setHeroImageThumbnail('');
                          setImagePreview(null);
                          setFormData(prev => ({ ...prev, cover_image_url: '' }));
                        }}
                        className="absolute top-2 right-20 bg-red-500 text-white px-3 py-1 rounded-lg text-sm hover:bg-red-600"
                      >
                        {t('remove')}
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full">
                      <Upload className="mb-2 text-[var(--theme-primary)]" size={48} />
                      <p className="text-[var(--theme-primary)] font-semibold mb-2 text-sm">
                        {t('select_hero_image')}
                      </p>
                      <button
                        type="button"
                        onClick={() => setShowHeroCropper(true)}
                        className="inline-block px-6 py-2 bg-[var(--theme-primary)] text-white rounded-lg cursor-pointer hover:bg-[#023872] transition-colors"
                      >
                        {t('choose_image')}
                      </button>
                      <p className="text-xs text-gray-600 mt-2">
                        {t('image_aspect_hint')}
                      </p>

                      {/* Video Thumbnail Option - Show when video URL has a thumbnail */}
                      {videoThumbnailUrl && articleFormat === 'video' && (
                        <div className="mt-4 w-full">
                          <div className="border-t border-gray-300 pt-3 mt-3">
                            <p className="text-xs text-gray-600 mb-2">{t('or_use_video_thumbnail')}</p>
                            <button
                              type="button"
                              onClick={handleUseVideoThumbnail}
                              className="w-full px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 transition-colors"
                            >
                              {t('use_video_thumbnail')}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {errors.image && (
                    <p className="text-red-500 text-sm mt-2">{errors.image}</p>
                  )}
                </div>

                {/* Card-image helper — website format only: nudge the owner to screenshot their site */}
                {articleFormat === 'website' && (
                  <p className="text-xs text-gray-600 mt-2">
                    {t('website_card_image_hint')}
                  </p>
                )}

                {/* Image Caption + Credit */}
                {imagePreview && (
                  <div className="mt-4 space-y-3">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        {t('image:caption.label')}
                      </label>
                      <input
                        type="text"
                        value={formData.image_caption}
                        onChange={(e) => handleInputChange('image_caption', e.target.value)}
                        placeholder={t('image:caption.placeholder')}
                        maxLength={ARTICLE_IMAGE_CAPTION_MAX}
                        className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:border-[var(--theme-primary)] text-sm"
                      />
                      <p className="text-gray-500 text-xs mt-1">
                        {t('character_count', { current: formData.image_caption.length, max: ARTICLE_IMAGE_CAPTION_MAX })}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        {t('image:credit.label')}
                      </label>
                      <input
                        type="text"
                        value={heroImageCredit}
                        onChange={(e) => setHeroImageCredit(e.target.value)}
                        placeholder={t('image:credit.placeholder')}
                        maxLength={ARTICLE_IMAGE_CREDIT_MAX}
                        className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:border-[var(--theme-primary)] text-sm"
                      />
                      <p className="text-gray-500 text-xs mt-1">
                        {t('character_count', { current: heroImageCredit.length, max: ARTICLE_IMAGE_CREDIT_MAX })}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Location Info - only shown in group mode */}
              {!isLocalityMode && (
                <div className="mt-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('location')}
                  </label>
                  <div className="bg-white rounded-lg p-4 border border-gray-300">
                    <div className="flex items-start gap-2">
                      <MapPin className="text-blue-600 mt-1" size={20} />
                      <div className="flex-1">
                        <p className="text-sm text-gray-700">
                          {t('locality_from_group')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

            </div>

            {/* Middle Column - Flexible - shows SECOND on mobile */}
            <div className="space-y-3 flex-1" style={isMobile ? { order: 2 } : {}}>
              {/* Title */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('title')}
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder={t('title_placeholder')}
                  maxLength={ARTICLE_TITLE_MAX}
                  className={`w-full px-4 py-3 rounded-lg border ${
                    errors.title ? 'border-red-500' : 'border-gray-300'
                  } focus:outline-none focus:border-[var(--theme-primary)]`}
                />
                {errors.title && (
                  <p className="text-red-500 text-sm mt-1">{errors.title}</p>
                )}
                <p className="text-gray-500 text-xs mt-1">
                  {t('character_count', { current: formData.title.length, max: ARTICLE_TITLE_MAX })}
                </p>
              </div>

              {/* Introduction (was Summary) — hidden for website embeds (form is Title + URL only) */}
              {articleFormat !== 'website' && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('introduction')}
                </label>
                <textarea
                  value={formData.summary}
                  onChange={(e) => handleInputChange('summary', e.target.value)}
                  placeholder={t('introduction_placeholder')}
                  maxLength={ARTICLE_SUMMARY_MAX}
                  rows={3}
                  className={`w-full px-4 py-3 rounded-lg border ${
                    errors.summary ? 'border-red-500' : 'border-gray-300'
                  } focus:outline-none focus:border-[var(--theme-primary)] resize-none`}
                />
                {errors.summary && (
                  <p className="text-red-500 text-sm mt-1">{errors.summary}</p>
                )}
                <p className="text-gray-500 text-xs mt-1">
                  {t('character_count', { current: formData.summary.length, max: ARTICLE_SUMMARY_MAX })}
                </p>
              </div>
              )}

              {/* Your Story — hidden for website embeds (form is Title + URL only) */}
              {articleFormat !== 'website' && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('your_story')}
                </label>
                {/* Formatting toolbar + Insert Image */}
                <div className="mb-2 flex items-center gap-1 flex-wrap">
                  <FormattingToolbar onFormat={applyFormatting} />
                  <button
                    type="button"
                    onClick={() => setShowInlineCropper(true)}
                    className="px-4 py-2 bg-teal-500 text-white rounded-lg text-sm font-semibold hover:bg-teal-600 transition-colors"
                  >
                    {t('insert_image')}
                  </button>
                </div>
                <textarea
                  ref={contentTextareaRef}
                  value={formData.content}
                  onChange={(e) => {
                    handleInputChange('content', e.target.value);
                    setContentCursorPos(e.target.selectionStart);
                  }}
                  onSelect={(e) => setContentCursorPos(e.target.selectionStart)}
                  onClick={(e) => setContentCursorPos(e.target.selectionStart)}
                  onKeyUp={(e) => setContentCursorPos(e.target.selectionStart)}
                  placeholder={articleFormat === 'video' ? t('content_placeholder_video') : t('content_placeholder_text')}
                  maxLength={articleFormat === 'video' ? ARTICLE_CONTENT_VIDEO_MAX : ARTICLE_CONTENT_MAX}
                  rows={12}
                  className={`w-full px-4 py-3 rounded-lg border ${
                    errors.content ? 'border-red-500' : 'border-gray-300'
                  } focus:outline-none focus:border-[var(--theme-primary)] resize-none`}
                />
                {errors.content && (
                  <p className="text-red-500 text-sm mt-1">{errors.content}</p>
                )}
                <p className="text-gray-500 text-xs mt-1">
                  {t('character_count', { current: formData.content.length, max: articleFormat === 'video' ? ARTICLE_CONTENT_VIDEO_MAX : ARTICLE_CONTENT_MAX })}
                </p>

                {/* Inline Images Gallery */}
                {inlineImages.length > 0 && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-sm font-semibold text-gray-700 mb-3">
                      {t('inserted_images', { count: inlineImages.length })}
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {inlineImages.map((img) => (
                        <div key={img.id} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                          {/* Thumbnail + delete + size controls */}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flexShrink: 0 }}>
                            <div style={{ position: 'relative', width: '80px', height: '80px' }}>
                              <img
                                src={img.thumbnailUrl || img.displayUrl}
                                alt=""
                                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '6px', border: '1px solid #e5e7eb' }}
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  setInlineImages(inlineImages.filter(i => i.id !== img.id));
                                  setFormData(prev => ({
                                    ...prev,
                                    content: prev.content
                                      .replace(new RegExp(`\\n?\\[IMG:${img.id}\\]\\n?`, 'g'), '')
                                      .replace(new RegExp(`\\n?\\[IMAGE:${img.id}\\]\\n?`, 'g'), '')
                                  }));
                                }}
                                style={{
                                  position: 'absolute',
                                  top: '4px',
                                  right: '4px',
                                  width: '20px',
                                  height: '20px',
                                  backgroundColor: 'rgba(220, 38, 38, 0.9)',
                                  border: 'none',
                                  borderRadius: '50%',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <X size={12} color="white" />
                              </button>
                            </div>
                            {/* Size toggle buttons */}
                            <div style={{ display: 'flex', gap: '2px', justifyContent: 'center' }}>
                              {['S', 'M', 'L'].map(size => (
                                <button
                                  key={size}
                                  type="button"
                                  onClick={() => handleInlineImageSize(img.id, size)}
                                  style={{
                                    width: '24px',
                                    height: '20px',
                                    fontSize: '10px',
                                    fontWeight: '600',
                                    border: 'none',
                                    borderRadius: '3px',
                                    cursor: 'pointer',
                                    backgroundColor: (img.size || 'L') === size ? 'var(--theme-primary)' : '#e5e7eb',
                                    color: (img.size || 'L') === size ? 'white' : '#6b7280'
                                  }}
                                >
                                  {size}
                                </button>
                              ))}
                            </div>
                          </div>
                          {/* Caption + Credit fields */}
                          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <input
                              type="text"
                              value={img.caption || ''}
                              onChange={(e) => handleInlineImageCaption(img.id, e.target.value)}
                              placeholder={t('image:caption.placeholder')}
                              maxLength={ARTICLE_IMAGE_CAPTION_MAX}
                              style={{
                                width: '100%',
                                padding: '4px 8px',
                                fontSize: '12px',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                outline: 'none'
                              }}
                            />
                            <input
                              type="text"
                              value={img.credit || ''}
                              onChange={(e) => handleInlineImageCredit(img.id, e.target.value)}
                              placeholder={t('image:credit.placeholder')}
                              maxLength={ARTICLE_IMAGE_CREDIT_MAX}
                              style={{
                                width: '100%',
                                padding: '4px 8px',
                                fontSize: '12px',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                outline: 'none'
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-3">
                      {t('image_size_info')}
                    </p>
                  </div>
                )}
              </div>
              )}

              {/* Tags */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('tags')}
                </label>
                <p className="text-sm text-gray-600 mb-3">
                  {t('tags_hint')}
                </p>

                {/* Input Area */}
                <div className="flex gap-3 mb-4">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                    placeholder={t('tags_placeholder')}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                  <button
                    onClick={handleAddTag}
                    className="bg-blue-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-blue-700 transition-colors"
                  >
                    {t('add')}
                  </button>
                </div>

                {/* AI Prompt Helper */}
                <div className="mb-4 flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <button
                    type="button"
                    onClick={handleCopyAIPrompt}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors"
                  >
                    {t('copy_ai_prompt')}
                  </button>
                  <p className="text-xs text-gray-600 flex-1">
                    {t('ai_prompt_help')}
                  </p>
                </div>

                {/* Tag Pills Display Area */}
                <div className="bg-white rounded-lg p-4 min-h-[120px] border border-gray-300">
                  <div className="flex flex-wrap gap-2">
                    {formData.article_tags.map((tag, index) => (
                      <button
                        key={index}
                        onClick={() => handleRemoveTag(tag)}
                        className="bg-teal-500 text-white px-4 py-2 rounded-full flex items-center gap-2 hover:bg-teal-600 transition-colors text-sm font-medium"
                      >
                        <span>{tag}</span>
                        <span className="text-lg leading-none">×</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Submit to Group / Locality context */}
              {isLocalityMode ? (
                // Own-parish addenda 1–2: the parish the post is stored in and appears in, chosen
                // from the browsed (else home) area's parishes; default its own parish.
                <ParishSelect
                  label={t('publishing_to')}
                  hint={t('locality_feed_info')}
                  options={homeLocalitySource === 'area-choice' ? homeLocalityBoundaries : []}
                  value={homeLocalityId}
                  onChange={handleLocalityPick}
                  disabled={isPlatformContent}
                  readOnlyName={homeLocalityName || selectedArea?.display_name || currentContext?.name || t('your_home_locality')}
                />
              ) : (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('submit_to_group')}
                  </label>
                  <select
                    value={formData.community_id}
                    onChange={(e) => handleInputChange('community_id', e.target.value)}
                    disabled={isPlatformContent}
                    className={`w-full px-4 py-3 rounded-lg border ${
                      errors.community_id ? 'border-red-500' : 'border-gray-300'
                    } focus:outline-none focus:border-[var(--theme-primary)]`}
                  >
                    <option value="">{t('select')}</option>
                    {savedGroupUnavailable && (
                      <option value={savedCommunityId} disabled>{t('common:saved_group_unavailable_option')}</option>
                    )}
                    {groups.map((group) => (
                      <option key={group.id} value={group.id}>
                        {group.name}
                      </option>
                    ))}
                  </select>
                  {savedGroupUnavailable && (
                    <p role="status" className="text-sm mt-1" style={{ color: '#92400E' }}>{t('common:saved_group_unavailable')}</p>
                  )}
                  {isPlatformContent && (
                    <p role="status" className="text-sm mt-1" style={{ color: tokens.colors.textSecondary }}>{t('common:platform_content_notice')} {t('common:platform_content_group_locked')}</p>
                  )}
                  {errors.community_id && (
                    <p className="text-red-500 text-sm mt-1">{errors.community_id}</p>
                  )}
                </div>
              )}

              {/* #72: where group content will appear. Public group: one boundary → a read-only
                  line; several → a picker limited to the group's own boundaries (interim, until
                  #73). Private group: the members-only line. No location → the notice.
                  A failed lookup → an error, and saving is blocked. */}
              {!homeLocalityLoading && isGroupContent && homeLocalitySource === 'group-error' && (
                <p className="text-red-500 text-sm mt-3">{t('common:generic_error')}</p>
              )}
              {/* A private group's content is for its members only — that line takes precedence
                  over the unlocated notice, whose "show its content locally" does not apply. */}
              {!homeLocalityLoading && isGroupContent && isPrivateGroupContent && homeLocalitySource !== 'group-error' && (
                <p className="text-gray-500 text-sm mt-3">{t('group_content_members_only')}</p>
              )}
              {!homeLocalityLoading && isGroupContent && !isPrivateGroupContent && groupLocalityStatus === 'unlocated' && !homeLocalityId && (
                <p className="text-gray-500 text-sm mt-3">{t('group_unlocated_notice')}</p>
              )}
              {!homeLocalityLoading && isGroupContent && !isPrivateGroupContent && groupLocalityStatus === 'located' && homeLocalityBoundaries.length <= 1 && homeLocalityId && homeLocalityName && (
                <p className="text-gray-500 text-sm mt-3">{t('group_content_appears_in', { locality: homeLocalityName })}</p>
              )}

              {/* Step 7B: Boundary picker (several boundaries: the browsed or home area, or a
                  public group's own boundaries) */}
              {!homeLocalityLoading && homeLocalityBoundaries.length > 1 && homeLocalitySource !== 'no-home' && homeLocalitySource !== 'area-choice' && !isPrivateGroupContent && (
                <div className="mt-3">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('locality_picker_label')}
                  </label>
                  <select
                    value={homeLocalityId || ''}
                    onChange={(e) => handleLocalityPick(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-[var(--theme-primary)]"
                  >
                    {!homeLocalityId && <option value="">{t('select')}</option>}
                    {homeLocalityBoundaries.map((b) => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Step 7B: No-home soft block */}
              {!homeLocalityLoading && homeLocalitySource === 'no-home' && (
                <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm text-amber-800 mb-3">{t('no_home_prompt')}</p>
                  <button
                    type="button"
                    onClick={() => window.dispatchEvent(new CustomEvent('ournet:navigate', { detail: { view: 'locations' } }))}
                    className="px-4 py-2 bg-[var(--theme-primary)] text-white rounded-lg text-sm font-semibold hover:bg-[#023872] transition-colors"
                  >
                    {t('no_home_button')}
                  </button>
                </div>
              )}
            </div>

            {/* Right Column - Fixed 300px (Article Format Selection) - Hidden on mobile (shown at top instead) */}
            {!isMobile && (
              <div className="space-y-6" style={{ width: '300px', flexShrink: 0 }}>
                {/* Article Format Radio Buttons */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    {t('article_format')}
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="articleFormat"
                        value="text"
                        checked={articleFormat === 'text'}
                        onChange={(e) => handleFormatChange(e.target.value)}
                        className="w-5 h-5 text-[var(--theme-primary)] focus:ring-[var(--theme-primary)] cursor-pointer"
                      />
                      <span className="ml-2 text-sm font-medium text-gray-700">{t('text')}</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="articleFormat"
                        value="video"
                        checked={articleFormat === 'video'}
                        onChange={(e) => handleFormatChange(e.target.value)}
                        className="w-5 h-5 text-[var(--theme-primary)] focus:ring-[var(--theme-primary)] cursor-pointer"
                      />
                      <span className="ml-2 text-sm font-medium text-gray-700">{t('video')}</span>
                    </label>
                    {/* Website format — group content only (embeds carry a community_id) */}
                    {!isLocalityMode && (
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="articleFormat"
                          value="website"
                          checked={articleFormat === 'website'}
                          onChange={(e) => handleFormatChange(e.target.value)}
                          className="w-5 h-5 text-[var(--theme-primary)] focus:ring-[var(--theme-primary)] cursor-pointer"
                        />
                        <span className="ml-2 text-sm font-medium text-gray-700">{t('website')}</span>
                      </label>
                    )}
                  </div>
                </div>

                {/* Video URL Field - Only visible when Video format selected */}
                {articleFormat === 'video' && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Video URL
                    </label>
                    <p className="text-xs text-gray-600 mb-3">
                      Paste a link from YouTube, Vimeo, or Framatube
                    </p>
                    <input
                      type="url"
                      value={formData.video_url}
                      onChange={(e) => handleInputChange('video_url', e.target.value)}
                      placeholder="https://youtube.com/watch?v=..."
                      className={`w-full px-4 py-3 rounded-lg border ${
                        errors.video_url ? 'border-red-500' : 'border-gray-300'
                      } focus:outline-none focus:border-[var(--theme-primary)] text-sm`}
                    />
                    {errors.video_url && (
                      <p className="text-red-500 text-xs mt-1">{errors.video_url}</p>
                    )}
                    {formData.video_url && !errors.video_url && (
                      <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <p className="text-xs text-blue-700 font-semibold mb-1">Video will be embedded:</p>
                        <p className="text-xs text-blue-600 break-all">{formData.video_url}</p>
                      </div>
                    )}
                    <div className="mt-3 bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-600 font-semibold mb-2">{t('supported_platforms')}</p>
                      <ul className="text-xs text-gray-500 space-y-1">
                        <li>• YouTube</li>
                        <li>• Vimeo</li>
                        <li>• PeerTube (Framatube)</li>
                      </ul>
                    </div>
                    <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <p className="text-xs text-yellow-700 font-semibold mb-1">Note:</p>
                      <p className="text-xs text-yellow-600">
                        Video articles have a 500 character limit for story content
                      </p>
                    </div>
                  </div>
                )}

                {/* Website URL Field - Only visible when Website format selected */}
                {articleFormat === 'website' && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('website_url')}
                    </label>
                    <p className="text-xs text-gray-600 mb-3">
                      {t('website_url_hint')}
                    </p>
                    <input
                      type="url"
                      value={formData.website_url}
                      onChange={(e) => handleInputChange('website_url', e.target.value)}
                      placeholder={t('website_url_placeholder')}
                      className={`w-full px-4 py-3 rounded-lg border ${
                        errors.website_url ? 'border-red-500' : 'border-gray-300'
                      } focus:outline-none focus:border-[var(--theme-primary)] text-sm`}
                    />
                    {errors.website_url && (
                      <p className="text-red-500 text-xs mt-1">{errors.website_url}</p>
                    )}
                    {formData.website_url && !errors.website_url && (
                      <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <p className="text-xs text-blue-700 font-semibold mb-1">{t('website_will_embed')}</p>
                        <p className="text-xs text-blue-600 break-all">{formData.website_url}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

        </div>
      </div>

      {/* Footer with Preview Button */}
      <FooterContainer viewType="form" isMobile={isMobile}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: isMobile ? '0 8px' : '0 40px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: isMobile ? '8px' : '16px'
          }}>
            {/* Preview Button */}
            <StandardButton
              variant="utility"
              iconName="Eye"
              label={t('preview')}
              onClick={() => setShowPreview(true)}
            />

            {/* Save as Draft */}
            <StandardButton
              variant="utility"
              iconName="Save"
              label={loading ? t('saving') : t('save_draft')}
              onClick={handleSaveAsDraft}
              disabled={loading}
            />

            {/* Cancel */}
            <StandardButton
              variant="destructive"
              iconName="X"
              label={t('cancel')}
              onClick={handleCancel}
              confirmBefore={false}
            />

            {/* Publish / Save and Exit — also disabled until the home-locality lookup resolves,
                so the minimal Title+URL website form can't submit before homeLocalityId is set
                and trip validateForm's home_locality guard (check 901). */}
            <StandardButton
              variant="action"
              iconName={mode === 'edit' ? 'Save' : 'Send'}
              label={mode === 'edit' ? t('save_and_exit') : t('publish')}
              onClick={handlePostArticle}
              disabled={loading || homeLocalityLoading || isNotAuthor}
            />

            {/* #78 item 3 / item 6: why Save cannot go through yet, beside the button. */}
            {(isNotAuthor || homeLocalityLoading) && (
              <span role="status" className="text-sm" style={{ color: isNotAuthor ? '#B91C1C' : '#4B5563', maxWidth: isMobile ? '120px' : '260px' }}>
                {isNotAuthor ? t('only_author_can_save') : t('loading_places')}
              </span>
            )}
          </div>
        </div>
      </FooterContainer>

      {leaveDialog}

      <ConfirmationModal
        visible={modal.visible}
        title={modal.title || t('confirmation')}
        message={modal.message}
        okLabel={modal.okLabel}
        cancelLabel={modal.cancelLabel}
        danger={modal.danger}
        onOk={() => {
          try { modal.onOk && modal.onOk(); } catch (e) { console.error(e); setModal({ visible: false, message: '', onOk: null }); setPendingFormatChange(null); }
        }}
        onCancel={() => {
          try {
            if (modal.onCancel) modal.onCancel();
            else { setModal({ visible: false, message: '', onOk: null }); setPendingFormatChange(null); }
          } catch (e) {
            console.error(e);
            setModal({ visible: false, message: '', onOk: null });
            setPendingFormatChange(null);
          }
        }}
      />

      {/* NEW - Hero Image Cropper Modal */}
      <ImageCropper
        isOpen={showHeroCropper}
        onClose={() => setShowHeroCropper(false)}
        onSave={handleHeroImageSave}
        aspectMode="hero"
        bucket="hero-images"
        userId={userId}
      />

      {/* NEW - Inline Image Cropper Modal */}
      <ImageCropper
        isOpen={showInlineCropper}
        onClose={() => setShowInlineCropper(false)}
        onSave={handleInlineImageSave}
        aspectMode="inline"
        bucket="inline-images"
        userId={userId}
      />

      {/* Article Preview Modal */}
      <ArticlePreviewModal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        formData={formData}
        heroImageDisplay={heroImageDisplay}
        heroImageCredit={heroImageCredit}
        inlineImages={inlineImages}
        groupName={groups.find(g => g.id === formData.community_id)?.name}
      />
    </div>
  );
};

export default AddEditArticle;
